import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import LogoutModal from "./LogoutModal";
import CareLineProfileModal from "./CareLineProfileModal";
import ClinicBrandLogo from "../common/ClinicBrandLogo";

/**
 * Careline gold-standard navbar.
 * @param {string} [slug] — When set, links are tenant-scoped (/:slug/...).
 * @param {string} [primaryColor] — Clinic accent (hex).
 */
const CareLineHeader = ({
    activePage,
    logo,
    clinicName,
    slug = null,
    primaryColor = "#06B6D4",
}) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [user, setUser] = useState(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const isTenant = Boolean(slug);
    const accent = primaryColor || "#06B6D4";

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };

        const storedUser = localStorage.getItem("user");
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (e) {
                console.error("Error parsing user data", e);
            }
        }

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const handleLogoutClick = () => {
        setIsDropdownOpen(false);
        setIsLogoutModalOpen(true);
    };

    const confirmLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        if (isTenant) {
            window.location.href = `/${slug}/login`;
        } else {
            window.location.href = "/careline/login";
        }
    };

    const handleProfileSave = (updatedData) => {
        const newUserCtx = { ...user, ...updatedData };
        setUser(newUserCtx);
        localStorage.setItem("user", JSON.stringify(newUserCtx));
        setIsProfileModalOpen(false);
    };

    const getInitials = (name) => {
        if (!name) return "?";
        return name
            .split(" ")
            .map((n) => n[0])
            .slice(0, 2)
            .join("")
            .toUpperCase();
    };

    const navLinks = isTenant
        ? [
              { to: `/${slug}`, label: "Layanan", key: "layanan" },
              { to: `/${slug}/poliklinik`, label: "Poliklinik", key: "polyclinic" },
              { to: `/${slug}/dokter`, label: "Dokter", key: "dokter" },
              { to: `/${slug}/faq`, label: "FAQ", key: "faq" },
          ]
        : [
              { to: "/careline", label: "Layanan", key: "layanan" },
              { to: "/polyclinics", label: "Poliklinik", key: "polyclinic" },
              { to: "/doctors", label: "Dokter", key: "dokter" },
              { to: "/careline-faq", label: "FAQ", key: "faq" },
          ];

    const homeHref = isTenant ? `/${slug}` : "/careline";

    const navClass = (key) =>
        `relative px-3.5 py-2 text-[13.5px] transition-colors duration-150 rounded-lg ${
            activePage === key
                ? "font-semibold"
                : "text-slate-500 font-medium hover:bg-cyan-50/50"
        }`;

    const NavItem = ({ to, label, navKey }) => (
        <Link
            to={to}
            className={navClass(navKey)}
            style={{
                color: activePage === navKey ? accent : undefined,
            }}
        >
            {label}
            {activePage === navKey && (
                <span
                    className="absolute bottom-[5px] left-1/2 -translate-x-1/2 w-[16px] h-[2px] rounded-full"
                    style={{ backgroundColor: accent }}
                />
            )}
        </Link>
    );

    return (
        <header
            className={`w-full sticky top-0 z-[100] transition-all duration-300 ${
                isScrolled
                    ? "bg-white/70 backdrop-blur-xl shadow-sm border-b border-cyan-100/40"
                    : "bg-white border-b border-slate-100"
            }`}
        >
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-10 lg:px-16 xl:px-[60px] h-[68px] flex items-center justify-between gap-4">
                <Link to={homeHref} className="flex-shrink-0">
                    <ClinicBrandLogo
                        logoUrl={logo}
                        clinicName={clinicName}
                        fallbackText={isTenant ? slug : "CareLine"}
                        imageClassName="w-auto h-[36px] max-w-[150px] object-contain"
                        textClassName="text-lg font-bold text-slate-800"
                    />
                </Link>

                <nav className="hidden md:flex items-center gap-0.5">
                    {navLinks.map(({ to, label, key }) => (
                        <NavItem key={key} to={to} label={label} navKey={key} />
                    ))}
                </nav>

                <div className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2">
                    {user ? (
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() =>
                                    setIsDropdownOpen(!isDropdownOpen)
                                }
                                className={`flex items-center gap-2 pl-1.5 pr-3 sm:pr-4 py-1.5 rounded-full border transition-all duration-200 ${
                                    isDropdownOpen
                                        ? "border-[#06B6D4] bg-cyan-50/60"
                                        : "border-slate-200 bg-slate-50 hover:border-[#06B6D4] hover:bg-cyan-50/40"
                                }`}
                                style={
                                    isDropdownOpen
                                        ? { borderColor: accent }
                                        : undefined
                                }
                            >
                                <div
                                    className="w-[28px] h-[28px] sm:w-[30px] sm:h-[30px] rounded-full flex items-center justify-center text-white text-[10px] sm:text-[11px] font-bold flex-shrink-0"
                                    style={{
                                        background: `linear-gradient(135deg, ${accent}, #0891B2)`,
                                    }}
                                >
                                    {getInitials(user.name)}
                                </div>
                                <span className="text-sm font-semibold text-slate-700 hidden md:block max-w-[100px] truncate">
                                    {user.name}
                                </span>
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className={`text-slate-400 transition-transform duration-200 flex-shrink-0 ${isDropdownOpen ? "rotate-180" : ""}`}
                                >
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                            </button>

                            {isDropdownOpen && (
                                <React.Fragment>
                                    <div
                                        className="fixed inset-0 z-[90]"
                                        onClick={() => setIsDropdownOpen(false)}
                                    />
                                    <div className="absolute right-0 top-full mt-2.5 w-52 bg-white border border-slate-200 rounded-2xl shadow-xl shadow-slate-200/60 z-[100] p-1.5 overflow-hidden">
                                        <div className="flex items-center gap-2.5 px-2.5 py-2.5 mb-1">
                                            <div
                                                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[12px] font-bold flex-shrink-0"
                                                style={{
                                                    background: `linear-gradient(135deg, ${accent}, #0891B2)`,
                                                }}
                                            >
                                                {getInitials(user.name)}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[13px] font-bold text-slate-800 truncate">
                                                    {user.name}
                                                </p>
                                                <p className="text-[11px] text-slate-400 font-medium">
                                                    Pasien
                                                </p>
                                            </div>
                                        </div>

                                        <div className="border-t border-slate-100 my-1" />

                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsDropdownOpen(false);
                                                setIsProfileModalOpen(true);
                                            }}
                                            className="w-full flex items-center gap-2.5 px-2.5 py-2 text-[13.5px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl transition-colors"
                                        >
                                            <span className="w-[26px] h-[26px] rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                                                <svg
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                                                    <circle
                                                        cx="12"
                                                        cy="7"
                                                        r="4"
                                                    />
                                                </svg>
                                            </span>
                                            Profil Saya
                                        </button>

                                        <div className="border-t border-slate-100 my-1" />

                                        <button
                                            type="button"
                                            onClick={handleLogoutClick}
                                            className="w-full flex items-center gap-2.5 px-2.5 py-2 text-[13.5px] font-medium text-red-500 hover:text-red-600 hover:bg-red-50/70 rounded-xl transition-colors"
                                        >
                                            <span className="w-[26px] h-[26px] rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                                                <svg
                                                    width="13"
                                                    height="13"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="#ef4444"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                                    <polyline points="16 17 21 12 16 7" />
                                                    <line
                                                        x1="21"
                                                        y1="12"
                                                        x2="9"
                                                        y2="12"
                                                    />
                                                </svg>
                                            </span>
                                            Keluar
                                        </button>
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    ) : (
                        <React.Fragment>
                            <Link
                                to={
                                    isTenant
                                        ? `/${slug}/register`
                                        : "/careline/register"
                                }
                                className="px-3.5 sm:px-5 py-1.5 sm:py-2 rounded-full border text-xs sm:text-sm font-semibold hover:bg-cyan-50 transition-colors whitespace-nowrap"
                                style={{ borderColor: accent, color: accent }}
                            >
                                Daftar
                            </Link>
                            <Link
                                to={isTenant ? `/${slug}/login` : "/careline/login"}
                                className="px-3.5 sm:px-5 py-1.5 sm:py-2 rounded-full text-white text-xs sm:text-sm font-semibold hover:opacity-90 transition-all shadow-md shadow-cyan-200/60 hover:-translate-y-px whitespace-nowrap"
                                style={{
                                    background: `linear-gradient(to right, ${accent}, #0891B2)`,
                                }}
                            >
                                Masuk
                            </Link>
                        </React.Fragment>
                    )}

                    <button
                        type="button"
                        className="md:hidden flex flex-col items-center justify-center w-9 h-9 rounded-xl border border-slate-200 bg-slate-50 hover:border-[#06B6D4] hover:bg-cyan-50/40 transition-all gap-[5px] flex-shrink-0"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        <span
                            className={`block w-4 h-[1.5px] bg-slate-500 rounded-full transition-all duration-200 ${isMobileMenuOpen ? "rotate-45 translate-y-[6.5px]" : ""}`}
                        />
                        <span
                            className={`block w-4 h-[1.5px] bg-slate-500 rounded-full transition-all duration-200 ${isMobileMenuOpen ? "opacity-0" : ""}`}
                        />
                        <span
                            className={`block w-4 h-[1.5px] bg-slate-500 rounded-full transition-all duration-200 ${isMobileMenuOpen ? "-rotate-45 -translate-y-[6.5px]" : ""}`}
                        />
                    </button>
                </div>
            </div>

            {isMobileMenuOpen && (
                <div className="md:hidden border-t border-slate-100 bg-white/95 backdrop-blur-xl px-4 sm:px-6 py-3">
                    {navLinks.map(({ to, label, key }) => (
                        <Link
                            key={key}
                            to={to}
                            className={`flex items-center justify-between py-3 text-[14px] border-b border-slate-50 last:border-0 transition-colors ${
                                activePage === key
                                    ? "font-semibold"
                                    : "text-slate-500 font-medium"
                            }`}
                            style={{
                                color: activePage === key ? accent : undefined,
                            }}
                        >
                            {label}
                            {activePage === key && (
                                <span
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{ backgroundColor: accent }}
                                />
                            )}
                        </Link>
                    ))}
                </div>
            )}

            <LogoutModal
                isOpen={isLogoutModalOpen}
                onClose={() => setIsLogoutModalOpen(false)}
                onConfirm={confirmLogout}
            />
            <CareLineProfileModal
                isOpen={isProfileModalOpen}
                onClose={() => setIsProfileModalOpen(false)}
                userData={user}
                onSave={handleProfileSave}
            />
        </header>
    );
};

export default CareLineHeader;
